import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const AuthContext = createContext(null);

// Normalize backend URL to handle various formats
const normalizeBackendBase = (rawValue) => {
  const fallback = 'http://localhost:8000';
  const raw = String(rawValue || '').trim();
  if (!raw) return fallback;
  const noTrailingSlash = raw.replace(/\/+$/g, '');
  return noTrailingSlash.replace(/\/api$/i, '');
};

const backendUrl = normalizeBackendBase(process.env.REACT_APP_BACKEND_URL);
const API = `${backendUrl}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const navigate = useNavigate();
  
  const checkAuth = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem('token');
        setToken(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  
  const applySession = (data) => {
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      return applySession(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Backend returns a structured detail when the email isn't verified yet.
      if (error.response?.status === 403 && detail?.code === 'email_not_verified') {
        const verifyError = new Error(detail.message || 'Please verify your email.');
        verifyError.code = 'email_not_verified';
        verifyError.email = detail.email || email;
        throw verifyError;
      }
      throw new Error(typeof detail === 'string' ? detail : 'Login failed');
    }
  };
  
  const register = async (name, email, password, phone, ageGroup, skillLevel) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name,
        email: email.trim().toLowerCase(),
        password,
        phone,
        age_group: ageGroup,
        skill_level: skillLevel,
      });
      // New accounts are NOT auto-logged in — they must verify their email first.
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      throw new Error(typeof detail === 'string' ? detail : 'Registration failed');
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { email, code });
      return applySession(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      throw new Error(typeof detail === 'string' ? detail : 'Verification failed');
    }
  };

  const resendVerification = async (email) => {
    try {
      const response = await axios.post(`${API}/auth/resend-verification`, { email });
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      throw new Error(typeof detail === 'string' ? detail : 'Could not resend code');
    }
  };
  
  const loginWithGoogle = async (credentialResponse) => {
    try {
      const response = await axios.post(`${API}/auth/google`, { 
        credential: credentialResponse.credential 
      });
      const data = response.data;
      
      localStorage.setItem('token', data.token);
      setToken(data.token);
      setUser(data.user);
      return data.user;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Google login failed');
    }
  };
  
  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    navigate('/');
  };
  
  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };
  
  const value = {
    user,
    loading,
    token,
    login,
    register,
    verifyEmail,
    resendVerification,
    loginWithGoogle,
    logout,
    updateUser,
    isAuthenticated: !!user
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
export const ProtectedRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { state: { from: location }, replace: true });
    }
  }, [loading, isAuthenticated, navigate, location]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : null;
};

export default AuthContext;
